import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Lock, Menu, X } from 'lucide-react'
import { AnimatePresence, MotionConfig, motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { LocalizedLink } from '@/components/LocalizedLink'
import { LanguageSelector } from '@/components/LanguageSelector'
import { ReservedAreaLink } from '@/components/ReservedAreaLink'
import { localizedRoutes, type RouteKey } from '@/i18n/routes'

// Voci del menu: ogni chiave esiste in src/i18n/routes.ts e ha una traduzione `nav.*`.
const navItems: { key: RouteKey; labelKey: string }[] = [
  { key: 'home', labelKey: 'nav.home' },
  { key: 'zambePong', labelKey: 'nav.zambePong' },
]

/** Marchio del sito: un tocco da laurea nello stile degli sticker (header pubblico e area riservata). */
export function BrandMark({ className = 'size-8' }: { className?: string }) {
  return (
    <svg viewBox="0 0 40 40" aria-hidden="true" className={className}>
      <path d="M11 19v7c0 3 4 5 9 5s9-2 9-5v-7" fill="var(--color-sea)" stroke="var(--color-paper)" strokeWidth="2.6" strokeLinejoin="round" />
      <path d="M3 15L20 7l17 8-17 8z" fill="var(--color-coral)" stroke="var(--color-paper)" strokeWidth="2.6" strokeLinejoin="round" />
      <path d="M20 15l10 4v8" fill="none" stroke="var(--color-paper)" strokeWidth="2.2" strokeLinecap="round" />
      <circle cx="30" cy="29" r="2.6" fill="var(--color-sun)" stroke="var(--color-paper)" strokeWidth="2" />
      <circle cx="20" cy="15" r="1.8" fill="var(--color-paper)" />
    </svg>
  )
}

export function Header() {
  const { t } = useTranslation()
  const location = useLocation()
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const currentSlug = location.pathname.split('/').filter(Boolean)[1] ?? ''
  const menuToggleRef = useRef<HTMLButtonElement>(null)
  const wasMobileOpenRef = useRef(false)

  useEffect(() => setIsMobileOpen(false), [location.pathname])

  useEffect(() => {
    document.body.style.overflow = isMobileOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMobileOpen])

  // Esc chiude il menu; mentre è aperto il resto della pagina è inert (Tab non può raggiungerlo);
  // alla chiusura il focus torna sul pulsante che l'ha aperto.
  useEffect(() => {
    const pageElements = document.querySelectorAll('main, footer')
    pageElements.forEach((el) => el.toggleAttribute('inert', isMobileOpen))
    if (!isMobileOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsMobileOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      pageElements.forEach((el) => el.removeAttribute('inert'))
    }
  }, [isMobileOpen])

  useEffect(() => {
    if (wasMobileOpenRef.current && !isMobileOpen) {
      menuToggleRef.current?.focus()
    }
    wasMobileOpenRef.current = isMobileOpen
  }, [isMobileOpen])

  const isActive = (key: RouteKey) => {
    const route = localizedRoutes[key]
    return route.it === currentSlug || route.en === currentSlug
  }

  // Niente backdrop-filter sull'header: renderebbe l'header il riferimento del pannello `fixed` (#site-menu),
  // che collasserebbe sui 64px dell'header invece di coprire lo schermo.
  return (
    <header className="fixed inset-x-0 top-0 z-50 bg-ink text-paper shadow-[0_1px_0_color-mix(in_oklch,var(--color-paper)_8%,transparent)]">
      <nav aria-label={t('aria.mainNavigation')} className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 md:h-20 lg:px-8">
        <div className="lg:flex-1">
          <LocalizedLink to="home" className="inline-flex items-center gap-2 font-display text-2xl font-bold md:text-[1.7rem]">
            <BrandMark />
            <span>Zambe <span className="text-coral">laureato</span></span>
          </LocalizedLink>
        </div>

        <div className="hidden items-center gap-1 lg:flex">
          {navItems.map(({ key, labelKey }) => (
            <LocalizedLink
              key={key}
              to={key}
              aria-current={isActive(key) ? 'page' : undefined}
              className={cn('group relative rounded-2xl px-3 py-2 text-sm font-medium transition-colors hover:bg-paper/10', isActive(key) && 'font-semibold')}
            >
              {t(labelKey)}
              <span className={cn('absolute inset-x-3 bottom-1 h-0.5 rounded-full transition-transform', 'bg-coral', isActive(key) ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100')} />
            </LocalizedLink>
          ))}
        </div>

        <div className="hidden items-center justify-end gap-2 lg:flex lg:flex-1">
          <LanguageSelector variant="desktop" />
          <ReservedAreaLink className="inline-flex items-center gap-1.5 rounded-full bg-coral px-4 py-1.5 text-sm font-bold text-white transition-colors hover:bg-coral-ink">
            <Lock className="size-3.5" aria-hidden="true" />
            {t('nav.reservedArea')}
          </ReservedAreaLink>
        </div>

        <button
          ref={menuToggleRef}
          type="button"
          onClick={() => setIsMobileOpen(!isMobileOpen)}
          className="rounded-2xl p-2 transition-colors hover:bg-paper/10 lg:hidden"
          aria-label={isMobileOpen ? t('aria.closeMenu') : t('aria.openMenu')}
          aria-expanded={isMobileOpen}
          aria-controls="site-menu"
        >
          {isMobileOpen ? <X className="size-6" /> : <Menu className="size-6" />}
        </button>
      </nav>

      {/* reducedMotion "user": con la preferenza attiva restano solo le dissolvenze, niente spostamenti. */}
      <MotionConfig reducedMotion="user">
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            id="site-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 top-16 z-40 flex flex-col overflow-y-auto bg-ink px-4 pt-4 pb-6 text-paper sm:px-6 md:top-20 lg:hidden"
          >
            <motion.ol
              aria-label={t('aria.mobileNavigation')}
              initial="hidden"
              animate="shown"
              variants={{ shown: { transition: { staggerChildren: 0.06, delayChildren: 0.05 } } }}
              className="flex-1"
            >
              {navItems.map(({ key, labelKey }) => (
                <motion.li
                  key={key}
                  variants={{ hidden: { opacity: 0, y: 12 }, shown: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } } }}
                  className="border-b border-paper/15"
                >
                  <LocalizedLink to={key} aria-current={isActive(key) ? 'page' : undefined} className="group flex items-center gap-4 py-4">
                    <span className={cn('flex-1 font-display text-[clamp(2rem,9vw,3rem)] leading-none tracking-tight transition-transform duration-300 ease-out-expo group-hover:translate-x-1', isActive(key) && 'text-coral')}>
                      {t(labelKey)}
                    </span>
                    {isActive(key) ? (
                      <span aria-hidden="true" className="flex items-center gap-1.5 font-body text-xs font-semibold tracking-[0.02em] text-coral">
                        <span className="size-1.5 rounded-full bg-coral motion-safe:animate-pulse" />
                        {t('nav.youAreHere')}
                      </span>
                    ) : (
                      <ArrowRight aria-hidden="true" className="size-5 text-paper/60 transition-transform duration-300 ease-out-expo group-hover:translate-x-1" />
                    )}
                  </LocalizedLink>
                </motion.li>
              ))}
            </motion.ol>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="mt-8"
            >
              <p className="inscription text-lg text-paper/70">{t('language.label')}</p>
              <span aria-hidden="true" className="ticket-perforation mt-2" />
              <div className="pt-1">
                <LanguageSelector variant="mobile" />
                <ReservedAreaLink className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-coral px-4 py-3.5 font-display text-lg font-semibold text-white transition-colors hover:bg-coral-ink">
                  <Lock className="size-4" aria-hidden="true" />
                  {t('nav.reservedArea')}
                </ReservedAreaLink>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </MotionConfig>
    </header>
  )
}
